import type { Dive } from "../../../../api/followup/Followup.typed";
import { toLocalDateKey } from "../../../../utils/dateTime";

/** YYYY-MM-DD for date inputs; never throws on invalid stored values. */
export function diveDateToInputValue(date?: Date | string | null): string {
  if (date == null || date === "") {
    return "";
  }

  const parsed = date instanceof Date ? date : new Date(date);
  if (Number.isNaN(parsed.getTime())) {
    return "";
  }

  return toLocalDateKey(parsed);
}

export function formatDiveDateForDisplay(date?: Date | string | null): string {
  const key = diveDateToInputValue(date);
  if (!key) return "—";
  const [y, m, d] = key.split("-");

  return `${d}/${m}/${y}`;
}

export function formatDiveMeasure(value: number | string | null | undefined, unit: string): string {
  const num = typeof value === "string" ? Number(value) : value;

  return typeof num === "number" && num > 0 ? `${num} ${unit}` : "—";
}

/** Parse a date input value as local noon (timezone-safe for display and API). */
export function parseDiveDateFromInput(value: string): Date | null {
  if (!value) {
    return null;
  }

  const parts = value.split("-").map((part) => parseInt(part, 10));
  const y = parts[0];
  const m = parts[1];
  const d = parts[2];
  if (!y || !m || !d) {
    return null;
  }

  const parsed = new Date(y, m - 1, d, 12, 0, 0);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }

  return parsed;
}

export function newDiveDefaultDate(): Date {
  const key = toLocalDateKey(new Date());
  const parts = key.split("-").map((part) => parseInt(part, 10));
  const y = parts[0];
  const m = parts[1];
  const d = parts[2];

  return new Date(y, m - 1, d, 12, 0, 0);
}

/** Keep only digits in a string (e.g. depth / time input) */
export function normalizeNumericInput(value: string): string {
  return value.replace(/[^0-9]/g, "");
}

/** Convert DiveDepth/DiveTime to numbers for the API */
export function normalizeDiveForSave(dive: Dive): Dive {
  return {
    ...dive,
    DiveDepth:
      typeof dive.DiveDepth === "string" && dive.DiveDepth !== ""
        ? Number(dive.DiveDepth)
        : typeof dive.DiveDepth === "number"
          ? dive.DiveDepth !== null
            ? dive.DiveDepth
            : 0
          : 0,
    DiveTime:
      typeof dive.DiveTime === "string" && dive.DiveTime !== ""
        ? Number(dive.DiveTime)
        : typeof dive.DiveTime === "number"
          ? dive.DiveTime !== null
            ? dive.DiveTime
            : 0
          : 0,
  };
}

export function normalizeDivesForSave(dives: Dive[]): Dive[] {
  return dives.map(normalizeDiveForSave);
}
